import React, { useEffect, useRef, useState } from "react"

export const Video = ({ videoSrc, isInsideTooltip = false }) => {
	const videoRef = useRef(null)

	const [showingVideo, setShowingVideo] = useState(false)

	const [currentTime, setCurrentTime] = useState(0)
	const [duration, setDuration] = useState(0)
	const [isDragging, setIsDragging] = useState(false)
	const [expanded, setExpanded] = useState(false)

	const handleTimeUpdate = () => {
		if (!videoRef?.current) return

		if (!isDragging) {
			setCurrentTime(videoRef.current.currentTime)
		}
	}

	const handleLoadedMetadata = () => {
		if (!videoRef?.current) return

		setDuration(videoRef.current.duration)
	}

	const handleSeek = (event) => {
		if (!videoRef.current) return

		const seekFraction = calculateSeekFraction(event)
		const seekTime = seekFraction * duration
		videoRef.current.currentTime = seekTime
		setCurrentTime(seekTime)
	}

	const handleDragStart = () => {
		setIsDragging(true)
	}

	const handleDragEnd = () => {
		setIsDragging(false)
	}

	const handleDrag = (event) => {
		if (isDragging) {
			const seekFraction = calculateSeekFraction(event)
			const seekTime = seekFraction * duration
			videoRef.current.currentTime = seekTime
			setCurrentTime(seekTime)
		}
	}

	const calculateSeekFraction = (event) => {
		const seekBarRect = event.target.getBoundingClientRect()
		return (event.clientX - seekBarRect.left) / seekBarRect.width
	}

	const togglePlay = (e, leftClick, vidRef) => {
		if (!vidRef.current) return

		try {
			e.preventDefault()

			if (leftClick) {
				if (vidRef.current.paused) {
					vidRef.current.play()
				} else {
					vidRef.current.pause()
				}
			}
		} catch (error) {
			console.error("Catch error in togglePlay", error)
		}
	}

	const enterFullscreen = () => {
		if (!videoRef?.current) return
		let videoReference = null

		if (isInsideTooltip) {
			videoReference = document.createElement("video")
			videoReference.src = videoSrc
			videoReference.controls = true
			videoReference.autoplay = true
			videoReference.muted = true
			videoReference.addEventListener("fullscreenchange", (e) => {
				if (!document.fullscreenElement) {
					videoReference.remove()
				}
			})
			document.body.appendChild(videoReference)
		} else {
			videoReference = videoRef.current
		}

		if (videoReference?.requestFullscreen) {
			videoReference.requestFullscreen()
		} else if (videoReference?.mozRequestFullScreen) {
			videoReference.mozRequestFullScreen()
		} else if (videoReference?.webkitRequestFullscreen) {
			videoReference.webkitRequestFullscreen()
		} else if (videoReference?.msRequestFullscreen) {
			videoReference.msRequestFullscreen()
		}
	}

	const expandVideoViewport = () => {
		if (!videoRef?.current) return

		if (expanded) {
			setExpanded(false)
		} else {
			setExpanded(true)
		}
	}

	useEffect(() => {
		setShowingVideo(true)
	}, [])

	const PlayButton = (
		<svg
			style={{ display: "block" }}
			width="1rem"
			height="1rem"
			viewBox="0 0 24 24"
		>
			<path
				fill="#fff"
				stroke="#aaa"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth="1.5"
				d="M6.906 4.537A.6.6 0 0 0 6 5.053v13.894a.6.6 0 0 0 .906.516l11.723-6.947a.6.6 0 0 0 0-1.032z"
			></path>
		</svg>
	)

	const PauseButton = (
		<svg
			style={{ display: "block" }}
			width="1rem"
			height="1rem"
			viewBox="0 0 24 24"
		>
			<path
				fill="#fff"
				stroke="#777"
				d="M10.25 5.5v13a1.75 1.75 0 0 1-1.75 1.75h-3a1.75 1.75 0 0 1-1.75-1.75v-13A1.76 1.76 0 0 1 5.5 3.75h3a1.75 1.75 0 0 1 1.75 1.75m10 0v13a1.75 1.75 0 0 1-1.75 1.75h-3a1.75 1.75 0 0 1-1.75-1.75v-13a1.76 1.76 0 0 1 1.75-1.75h3a1.75 1.75 0 0 1 1.75 1.75"
			></path>
		</svg>
	)

	const FullscreenButton = (
		<svg
			style={{ display: "block" }}
			width="1rem"
			height="1rem"
			viewBox="0 0 50 50"
		>
			<path
				fill="#fff"
				stroke="#555"
				d="M2 15.758V2h14.299l5.262 4h-8.769L22 15.758L16.299 21L7 12.251v8.769zM33.752 2H48v13.809l-4 5.261v-8.768L33.997 21.51l-5.364-5.456L37.259 7H28.49zM48 33.752V48H33.701l-5.262-4h8.769L28 33.997l5.701-5.364L43 37.259V28.49zM16.248 48H2V33.701l4-5.262v8.769L16.003 28l5.364 5.701L12.741 43h8.769z"
			></path>
		</svg>
	)

	return (
		<div
			className={`video-wrapper video-${showingVideo ? "showing" : "hiding"} ${expanded ? "expanded" : ""}`}
		>
			<video
				src={videoSrc}
				controls={false}
				autoPlay={true}
				muted={true}
				ref={videoRef}
				onClick={(e) => togglePlay(e, true, videoRef)}
				onTimeUpdate={handleTimeUpdate}
				onLoadedMetadata={handleLoadedMetadata}
			/>

			<div
				className="video-controls"
				style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}
			>
				<div
					style={{ width: "1rem", height: "1rem" }}
					onClick={(e) => togglePlay(e, true, videoRef)}
				>
					{videoRef?.current?.paused ? PlayButton : PauseButton}
				</div>
				<div
					className="seek-bar shadow border"
					onClick={handleSeek}
					onMouseDown={handleDragStart}
					onMouseUp={handleDragEnd}
					onMouseMove={handleDrag}
				>
					<div
						className="progress"
						style={{
							width: `${(currentTime / duration) * 100}%`,
							pointerEvents: "none",
						}}
					/>
				</div>
				<div
					style={{ width: "1rem", height: "1rem" }}
					onClick={enterFullscreen}
				>
					{FullscreenButton}
				</div>
			</div>

			<style>{`
				.video-wrapper {
					position: relative;
					width: 100%;
					height: 100%;
					transition: opacity 1s ease-in-out;
				}
				.video-wrapper video {
					box-shadow: 0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06);
					border: 1px solid #bbb;
					width: 100%;
					height: 100%;
					border-radius: 0.5rem;
					cursor: pointer;
					object-fit: cover;
				}
				.video-showing {
					opacity: 1;
				}
				.video-hiding {
					opacity: 0;
				}

				.video-controls {
					position: absolute;
					bottom: 1rem;
					left: 0.5rem;
					right: 0.5rem;
					height: 1rem;
					opacity: 0;
					cursor: pointer;
					transition: opacity 0.2s ease;
				}
				.video-wrapper:hover .video-controls {
					opacity: 1;
				}

				.seek-bar {
					flex: 1;
					height: 0.7rem;
					background-color: #ccc;
					cursor: pointer;
					position: relative;
					border-radius: 0.5rem;
					overflow: hidden;
					transition: opacity 0.2s ease;
				}
				.progress {
					height: 100%;
					background-color: #fff;
					border-radius: 4px;
					transition: width 0.1s ease;
				}

				.video-wrapper.expanded {
					display: flex;
					justify-content: center;
					align-items: center;
					padding: 10px;
					transition: all 0.3s ease;
					position: fixed;
					top: 0;
					left: 0;
					width: 100vw;
					height: 100vh;
					background-color: rgba(0, 0, 0, 0.8);
					z-index: 1000001;
				}
				.video-wrapper.expanded video {
					max-width: 100%;
					max-height: 100%;
					width: auto;
					height: auto;
					object-fit: contain;
				}
			`}</style>
		</div>
	)
}
